import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { User } from '@phosphor-icons/react';
import { sortHeights } from '@/lib/heights';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';
import { track, setAnalyticsUser } from '@/lib/track';
import CityInput from '@/components/CityInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Map onboarding gender to the admin-managed category name that houses their heights.
const GENDER_TO_CATEGORY = {
  male: 'men',
  female: 'women',
};

const OnboardingForm = ({ onComplete }) => {
  useBodyScrollLock();
  const [formData, setFormData] = useState({
    height: '',
    weight: '',
    weight_unit: 'lbs',  // Default to lbs
    phone: '',
    gender: '',
    city: ''
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/categories`)
      .then((res) => setCategories(res.data || []))
      .catch(() => setCategories([]));
  }, []);

  // Heights available in the dropdown depend on the currently-selected gender.
  // No gender → no options (user must pick gender first).
  const genderCategoryName = GENDER_TO_CATEGORY[formData.gender];
  const matchedCategory = genderCategoryName
    ? categories.find((c) => (c.name || '').toLowerCase() === genderCategoryName)
    : null;
  const heightOptions = matchedCategory?.heights?.length
    ? sortHeights(matchedCategory.heights)
    : [];

  // If the user changes gender and the previously selected height doesn't belong
  // to the new category, clear it.
  useEffect(() => {
    if (!formData.height) return;
    if (!heightOptions.includes(formData.height)) {
      setFormData((prev) => ({ ...prev, height: '' }));
    }
  }, [formData.gender, categories]);

  // GA4's recommended tutorial_begin/tutorial_complete pair is the closest fit
  // for a mandatory first-run setup flow, and it gives us the drop-off rate
  // between landing here and finishing — previously unmeasured.
  useEffect(() => {
    track('tutorial_begin', { flow: 'onboarding' });
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.height || !formData.weight || !formData.phone || !formData.gender) {
      // Which field people leave blank is the actionable half of a drop-off.
      track('form_error', {
        form_name: 'onboarding',
        missing_fields: ['height', 'weight', 'phone', 'gender']
          .filter((f) => !formData[f])
          .join(','),
      });
      toast.error('Please fill all fields');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/auth/complete-profile`,
        formData,
        { withCredentials: true }
      );

      if (response.data.success) {
        setAnalyticsUser(response.data.user);
        track('tutorial_complete', { flow: 'onboarding' });
        toast.success('Profile completed!');
        onComplete(response.data.user);
      }
    } catch (error) {
      console.error('Profile completion error:', error);
      track('form_error', {
        form_name: 'onboarding',
        error_status: error?.response?.status,
      });
      toast.error('Failed to complete profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div 
        className="bg-white w-full max-w-lg rounded-md border border-[#E4E1D9] p-8 max-h-[90vh] overflow-y-auto"
        data-lenis-prevent
        data-testid="onboarding-dialog"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F0EFEB] rounded-full mb-4">
            <User size={32} weight="duotone" className="text-[#2D302A]" />
          </div>
          <h2 
            className="text-3xl tracking-tighter mb-2" 
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="onboarding-title"
          >
            Complete Your Profile
          </h2>
          <p className="text-sm text-[#6B6A68]">
            Help us personalize your experience by sharing a few details
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Gender */}
          <div>
            <label
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid="gender-label"
            >
              Gender
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' }
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, gender: opt.value })}
                  className={`px-4 py-3 border rounded-md transition-all text-center ${
                    formData.gender === opt.value
                      ? 'bg-[#2D302A] text-white border-[#2D302A]'
                      : 'bg-white text-[#1A1A1A] border-[#E4E1D9] hover:border-[#2D302A]'
                  }`}
                  style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  data-testid={`gender-${opt.value}-button`}
                >
                  <span className="text-xs uppercase tracking-[0.2em]">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Height */}
          <div>
            <label 
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid="height-label"
            >
              Your Height (ft)
            </label>
            <select
              name="height"
              value={formData.height}
              onChange={handleInputChange}
              disabled={!formData.gender}
              className="w-full px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A] disabled:bg-[#F9F8F6] disabled:text-[#9E9C97] disabled:cursor-not-allowed"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid="height-select"
            >
              <option value="">
                {formData.gender ? 'Select your height' : 'Pick gender first'}
              </option>
              {heightOptions.map((h) => (
                <option key={h} value={h}>{h} ft</option>
              ))}
            </select>
          </div>

          {/* Weight */}
          <div>
            <label 
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid="weight-label"
            >
              Weight
            </label>
            <div className="flex gap-3">
              <input
                type="number"
                name="weight"
                value={formData.weight}
                onChange={handleInputChange}
                className="flex-1 px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
                placeholder="e.g., 180"
                min="30"
                max="500"
                data-testid="weight-input"
              />
              <select
                name="weight_unit"
                value={formData.weight_unit}
                onChange={handleInputChange}
                className="px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
                style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                data-testid="weight-unit-select"
              >
                <option value="lbs">LBS</option>
                <option value="kg">KG</option>
              </select>
            </div>
          </div>

          {/* Phone */}
          <div>
            <label 
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid="phone-label"
            >
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
              placeholder="e.g., +1 (555) 123-4567"
              data-testid="phone-input"
            />
          </div>

          {/* City — optional. This is the signup funnel, and a required extra
              field here costs completions; offline spots simply fall back to
              showing every city when it's blank. */}
          <div>
            <label
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            >
              City <span className="normal-case tracking-normal text-[#9A9A98]">(optional)</span>
            </label>
            <CityInput
              value={formData.city}
              onChange={(v) => setFormData((prev) => ({ ...prev, city: v }))}
              className="w-full px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
              testId="onboarding-city-input"
            />
            <p className="mt-1 text-[11px] text-[#9A9A98]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
              So we can show you offline spots near you.
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#2D302A] text-white px-6 py-4 rounded-md hover:bg-[#1A1C18] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            data-testid="complete-profile-button"
          >
            <span className="text-xs uppercase tracking-[0.2em]">
              {loading ? 'Completing...' : 'Complete Profile'}
            </span>
          </button>
        </form>

        <p className="text-xs text-center text-[#6B6A68] mt-6">
          This information helps us provide better product recommendations
        </p>
      </div>
    </div>
  );
};

export default OnboardingForm;